import React from "react";
import { toast } from "react-toastify";
import {
  sortBatchesByExpiry,
  isUsableBatch,
  toQuantity,
  emptyAllocation,
  buildAllocation,
  stockOptionValue,
  stockIdFromOptionValue,
  unsellableReason,
} from "../../helpers/batchAllocation";

/**
 * The extra batch rows for one order line.
 *
 * An order line's first batch stays on the row Retailerdashboard already
 * renders; everything beyond it lives here. The split exists so that adding or
 * editing a batch re-renders only this component - the rows around it are held
 * as pre-built elements in Retailerdashboard's state and are mutated straight
 * through the DOM, so React skips reconciling them only for as long as their
 * element references stay identical. Rebuilding them to squeeze in another row
 * would reset every figure the retailer had already typed.
 *
 * Rows here are ordinary controlled React rows, and they carry the same <td>
 * class names as the row above them, which is what the invoice collector in
 * Retailerdashboard reads (it walks "#theTable > tbody > tr" and picks cells out
 * by class). Matching those classes is the whole reason this needs no change
 * there beyond skipping the control row.
 */
class OrderBatchRows extends React.Component {
  constructor(props) {
    super(props);
    this.state = { rows: [] };
  }

  componentDidMount() {
    if (this.props.register) this.props.register(this.props.medicineId, this);
    //  The row above is committed in the same pass as this one, so its inputs
    //  are not in the document while this first renders and the figures read
    //  off them come back empty. Re-read once they are there.
    this.refresh();
  }

  /**
   * Re-read the primary row.
   *
   * That row is not React-rendered - Retailerdashboard writes into it through
   * the DOM - so nothing here can observe it changing. Retailerdashboard calls
   * this after it touches a batch or a billed quantity.
   */
  refresh = () => {
    this.forceUpdate();
  };

  componentWillUnmount() {
    if (this.props.register) this.props.register(this.props.medicineId, null);
  }

  /** Seed the rows FEFO worked out, or refresh after the primary row changed. */
  setRows = (rows) => {
    this.setState({ rows: rows || [] }, this.notify);
  };

  notify = () => {
    if (this.props.onChange) this.props.onChange();
  };

  /** Stock row the primary batch dropdown is pointing at, if any. */
  primaryStockId = () => {
    const el = document.getElementById("medbatch-" + this.props.medicineId);
    return el ? stockIdFromOptionValue(el.value) : null;
  };

  /**
   * Quantity already covered by the row Retailerdashboard renders.
   *
   * Nothing is covered until a batch is actually picked there. That row's
   * billed-quantity box is pre-filled with the ordered quantity, so counting it
   * unconditionally reported a line as fully allocated while its dropdown still
   * said "select batch" - which also drove remaining() to zero and disabled
   * "+ Add batch", leaving no way to allocate the line at all.
   */
  primaryQty = () => {
    if (!this.primaryStockId()) return 0;
    const el = document.getElementById("medbqty-" + this.props.medicineId);
    return el ? toQuantity(el.value) : 0;
  };

  allocatedTotal = () =>
    this.primaryQty() +
    this.state.rows.reduce((sum, row) => sum + toQuantity(row.qty), 0);

  remaining = () =>
    Math.max(0, toQuantity(this.props.orderedQty) - this.allocatedTotal());

  /** Batches still free to pick: in stock, in date, and not already used. */
  availableBatches = (currentKey) => {
    const taken = new Set();
    const primary = this.primaryStockId();
    if (primary) taken.add(primary);
    this.state.rows.forEach((row) => {
      if (row.key !== currentKey && row.stockId) taken.add(String(row.stockId));
    });

    return sortBatchesByExpiry(
      (this.props.stockRows || []).filter(
        (stock) => isUsableBatch(stock) && !taken.has(String(stock.id))
      )
    );
  };

  addRow = () => {
    if (this.remaining() <= 0) {
      toast.error("The ordered quantity is already covered by the batches selected");
      return;
    }
    this.setState({ rows: this.state.rows.concat(emptyAllocation()) }, this.notify);
  };

  removeRow = (key) => {
    this.setState(
      { rows: this.state.rows.filter((row) => row.key !== key) },
      this.notify
    );
  };

  updateRow = (key, changes) => {
    this.setState(
      {
        rows: this.state.rows.map((row) =>
          row.key === key ? Object.assign({}, row, changes) : row
        ),
      },
      this.notify
    );
  };

  /**
   * A batch was chosen for one of these rows. Fills the row from the stock
   * record and proposes the quantity still outstanding, capped at what the
   * batch holds - the same thing the retailer would otherwise type.
   */
  onBatchChange = (key, event) => {
    const stockId = stockIdFromOptionValue(event.target.value);
    if (!stockId) {
      //  Keep the row's key: it identifies this row to React and to every
      //  update below, so a fresh one here would remount the row.
      this.updateRow(key, Object.assign({}, emptyAllocation(), { key }));
      return;
    }

    const stock = (this.props.stockRows || []).find(
      (s) => String(s.id) === String(stockId)
    );
    if (!stock) return;

    const outstanding = this.remaining() + toQuantity(
      (this.state.rows.find((r) => r.key === key) || {}).qty
    );
    const proposed = Math.min(outstanding, toQuantity(stock.quantity));
    const filled = buildAllocation(stock, proposed);

    this.updateRow(key, Object.assign({}, filled, { key }));
  };

  onQtyChange = (key, value) => {
    this.updateRow(key, { qty: value });
  };

  /**
   * Billed quantity cannot exceed the batch. Checked on blur to match how the
   * row above behaves, rather than fighting the retailer mid-keystroke.
   */
  onQtyBlur = (key) => {
    const row = this.state.rows.find((r) => r.key === key);
    if (!row) return;

    if (toQuantity(row.qty) > toQuantity(row.available)) {
      toast.error("Billed Quantity should not greater than Available Quantity");
      this.updateRow(key, { qty: row.available });
      return;
    }
    this.notify();
  };

  onFieldChange = (key, field, value) => {
    this.updateRow(key, { [field]: value });
  };

  /**
   * Line figures.
   *
   * Deliberately the same arithmetic - and the same two assignments - as
   * caliculateInvoiceAmountsNewRows in Retailerdashboard, which is what runs on
   * the row above as soon as a batch is picked. That function puts the tax into
   * the "tax_inp" cell and the taxable value into "vatamt_inp"; the column
   * headings agree with it. Following it here keeps the two halves of a split
   * line consistent with each other and with every invoice raised so far.
   */
  computeRow = (row) => {
    const gstType = this.props.gstType;
    const mrp = toQuantity(row.mrp);
    const billedQty = toQuantity(row.qty);
    const discount = toQuantity(row.discount);

    const vatPercent =
      gstType === "GST"
        ? toQuantity(row.CGST) + toQuantity(row.SGST)
        : gstType === "IGST"
        ? toQuantity(row.IGST)
        : 0;

    const amt = mrp * billedQty;
    const netbill = amt - (amt * discount) / 100;
    const taxableAmt = netbill / (1 + vatPercent / 100);
    const vatAmount = netbill - taxableAmt;

    return {
      taxCell: vatAmount.toFixed(2),
      taxableCell: taxableAmt.toFixed(2),
      amountCell: netbill.toFixed(2),
    };
  };

  renderRow = (row) => {
    const { medicineId, gstType, productName } = this.props;
    const figures = this.computeRow(row);
    const rowId = medicineId + "__" + row.key;

    return (
      <tr key={row.key} id={"batchrow-" + rowId} class="batch_split_row">
        <td></td>
        <td class="item_inp">
          <div class="batch_split_label">{productName}</div>
        </td>

        <td class="batch_inp">
          <select
            class="form-control batches-list"
            id={"medbatch-" + rowId}
            value={row.optionValue || ""}
            onChange={(event) => this.onBatchChange(row.key, event)}
          >
            <option value="">select batch</option>
            {/*  The option value has to be the same "~"-joined string the row
                above uses: the invoice collector splits it by position. */}
            {this.availableBatches(row.key).map((stock) => (
              <option key={stock.id} value={stockOptionValue(stock)}>
                {stock.batch}/{stock.expiry_date}
              </option>
            ))}
          </select>
          <button
            type="button"
            class="batch_split_remove"
            title="Remove this batch"
            aria-label="Remove this batch"
            onClick={() => this.removeRow(row.key)}
          />
        </td>

        <td class="oqty_inp">
          <input
            type="text"
            class="form-control"
            id={"medqty-" + rowId}
            value=""
            readOnly={true}
          />
        </td>
        <td class="aqty_inp">
          <input
            type="text"
            class="form-control"
            id={"medaqty-" + rowId}
            value={row.available}
            readOnly={true}
          />
        </td>
        <td class="bqty_inp">
          <input
            type="text"
            class="form-control"
            id={"medbqty-" + rowId}
            value={row.qty}
            onChange={(event) => this.onQtyChange(row.key, event.target.value)}
            onBlur={() => this.onQtyBlur(row.key)}
          />
        </td>

        <td class="tax_inp">
          <input
            type="text"
            class="form-control"
            id={"medtaxamt-" + rowId}
            value={figures.taxCell}
            readOnly={true}
          />
        </td>

        {gstType === "GST" ? (
          <React.Fragment>
            <td class="cgst_inp">
              <input
                type="text"
                class="form-control"
                id={"medCGST-" + rowId}
                value={row.CGST}
                onChange={(event) =>
                  this.onFieldChange(row.key, "CGST", event.target.value)
                }
                onBlur={this.notify}
              />
            </td>
            <td class="sgst_inp">
              <input
                type="text"
                class="form-control"
                id={"medSGST-" + rowId}
                value={row.SGST}
                onChange={(event) =>
                  this.onFieldChange(row.key, "SGST", event.target.value)
                }
                onBlur={this.notify}
              />
            </td>
          </React.Fragment>
        ) : (
          <td class="igst_inp">
            <input
              type="text"
              class="form-control"
              id={"medIGST-" + rowId}
              value={row.IGST}
              onChange={(event) =>
                this.onFieldChange(row.key, "IGST", event.target.value)
              }
              onBlur={this.notify}
            />
          </td>
        )}

        <td class="vatamt_inp">
          <input
            type="text"
            class="form-control"
            id={"medvatamt-" + rowId}
            value={figures.taxableCell}
            readOnly={true}
          />
        </td>
        <td class="mrp_inp">
          <input
            type="text"
            class="form-control"
            id={"medmrp-" + rowId}
            value={row.mrp}
            readOnly={true}
          />
        </td>
        <td class="disc_inp">
          <input
            type="text"
            class="form-control"
            id={"meddiscount-" + rowId}
            value={row.discount}
            onChange={(event) =>
              this.onFieldChange(row.key, "discount", event.target.value)
            }
            onBlur={this.notify}
          />
        </td>
        <td class="amount_inp">
          <input
            type="text"
            class="form-control"
            id={"medamount-" + rowId}
            value={figures.amountCell}
            readOnly={true}
          />
        </td>
      </tr>
    );
  };

  render() {
    const { gstType, orderedQty } = this.props;
    //  Sl.no, Product, Batch, O/A/B Qty, tax, GST cells, taxable, MRP,
    //  discount, amount - two GST cells when the retailer is on GST.
    const columnCount = gstType === "GST" ? 13 : 12;
    const remaining = this.remaining();
    const allocated = this.allocatedTotal();
    const ordered = toQuantity(orderedQty);
    const canAdd = remaining > 0 && this.availableBatches(null).length > 0;

    //  Only worth saying while the line is actually unallocated. Once the
    //  retailer has deliberately picked an expired batch, repeating that the
    //  stock is expired is noise - the dropdown already labels it.
    const blocked =
      allocated === 0 ? unsellableReason(this.props.stockRows) : null;

    return (
      <React.Fragment>
        {this.state.rows.map(this.renderRow)}

        {/*  no-invoice-row keeps this out of the invoice collector, which
            otherwise treats every row in the table as a line item. */}
        <tr class="no-invoice-row batch_split_control">
          <td colSpan={columnCount}>
            <div class="batch_split_bar">
              <button
                type="button"
                class="batch_split_add"
                onClick={this.addRow}
                disabled={!canAdd}
              >
                + Add batch
              </button>
              <span
                class={
                  remaining > 0
                    ? "batch_split_status batch_split_short"
                    : "batch_split_status"
                }
              >
                {remaining > 0
                  ? "Allocated " + allocated + " of " + ordered + " - " + remaining + " still to allocate"
                  : "Allocated " + allocated + " of " + ordered}
              </span>
              {blocked ? (
                <span class="batch_split_blocked" title={blocked}>
                  {blocked} - pick one manually only if you mean to
                </span>
              ) : null}
            </div>
          </td>
        </tr>
      </React.Fragment>
    );
  }
}

export default OrderBatchRows;
